import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from './supabaseClient'; // Connecting directly to your Vault

const PLANET_ORDER = [
  "Rising Sign", "Sun", "Moon", "Mercury", "Venus", "Mars", 
  "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto", "Ceres", "Chiron"
];

const PLANET_SYMBOLS = {
  "Rising Sign": "ASC", "Sun": "☉", "Moon": "☽", "Mercury": "☿", "Venus": "♀", "Mars": "♂", 
  "Jupiter": "♃", "Saturn": "♄", "Uranus": "♅", "Neptune": "♆", "Pluto": "♇", 
  "Ceres": "⚳", "Chiron": "⚷"
};

export default function AscendanceDashboard({ profile, onLogMeditation }) {
  
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    alias: profile?.alias || "",
    birth_year: profile?.birth_year || "",
    birth_month: profile?.birth_month || "",
    birth_day: profile?.birth_day || "",
    city_of_origin: profile?.city_of_origin || ""
  });

  const alias = profile?.alias || "Master-Builder";
  
  // Format Birthday gracefully
  const hasBirthday = profile?.birth_year && profile?.birth_month && profile?.birth_day;
  const birthString = hasBirthday 
    ? `${profile.birth_month}/${profile.birth_day}/${profile.birth_year}`
    : "Coordinates Encrypted";
    
  const originCity = profile?.city_of_origin || "Unknown Sector";

  // Gamification Metrics
  const lifetimeCount = profile?.lifetime_meditations || 0;
  const monthlyCount = profile?.current_month_qualifying_count || 0;
  const unlockedKeys = profile?.unlocked_keys_array || [];

  // --- THE CRASH-PROOF 33-VERTEBRAE MATH ---
  // 10 logs = 1 Vertebra level. Capped at 33.
  const vertebraLevel = Math.min(Math.floor(lifetimeCount / 10) + 1, 33);
  
  const getSpinalRegion = (level) => {
    if (level >= 1 && level <= 4) return "Coccygeal (Root)";
    if (level >= 5 && level <= 9) return "Sacral (S5-S1)";
    if (level >= 10 && level <= 14) return "Lumbar (L5-L1)";
    if (level >= 15 && level <= 26) return "Thoracic (T12-T1)";
    if (level >= 27 && level <= 32) return "Cervical (C7-C2)";
    if (level === 33) return "Atlas (C1 - The Gateway)";
    return "Root";
  };

  const spinalRegion = getSpinalRegion(vertebraLevel);

  // Dynamic Title Logic based on Lifetime Meditations
  let currentTitle = "Initiate";
  let titleColor = "#8b949e";
  if (lifetimeCount >= 300) {
    currentTitle = "Ascended Master";
    titleColor = "#ffb3ff"; // Ethereal Pink
  } else if (lifetimeCount >= 130) {
    currentTitle = "Matrix Master";
    titleColor = "#00ff41"; // Matrix Green
  } else if (lifetimeCount >= 65) {
    currentTitle = "Somatic Guru";
    titleColor = "#00d4ff"; // Portal Blue
  }

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Securely grab the active user from Supabase directly
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      alert("Matrix Glitch: No active session found. Cannot save.");
      setSaving(false);
      return;
    }

    // Upsert the profile data!
    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        alias: editForm.alias,
        birth_year: parseInt(editForm.birth_year) || null,
        birth_month: parseInt(editForm.birth_month) || null,
        birth_day: parseInt(editForm.birth_day) || null,
        city_of_origin: editForm.city_of_origin
      });

    setSaving(false);

    if (error) {
      alert("Error saving coordinates: " + error.message);
    } else {
      setIsEditing(false);
      // Hard refresh the page to pull the fresh data from App.js seamlessly
      window.location.reload(); 
    }
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
      
      <Helmet>
        <title>The Sanctuary | {alias}'s Vault</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* --- 1. BESPOKE IDENTITY PLATE --- */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'flex-start', 
        marginBottom: '40px', 
        borderBottom: '1px solid #30363d', 
        paddingBottom: '30px' 
      }}>
        <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
          <div style={{ 
            height: '80px', 
            width: '80px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #ffca28 0%, #ff8c1a 100%)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            fontSize: '2rem', 
            color: '#05070a', 
            fontWeight: 'bold',
            boxShadow: '0 0 20px rgba(255, 202, 40, 0.2)'
          }}>
            {alias.charAt(0).toUpperCase()}
          </div>
          
          <div>
            <h2 style={{ color: '#ffffff', fontSize: '2.5rem', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '2px' }}>
              {alias}
            </h2>
            <div style={{ display: 'flex', gap: '15px', color: '#8b949e', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px', flexWrap: 'wrap' }}>
              <span><strong style={{color: '#c9d1d9'}}>Rank:</strong> <span style={{ color: titleColor, fontWeight: 'bold' }}>{currentTitle}</span></span>
              <span>|</span>
              <span><strong style={{color: '#c9d1d9'}}>Born:</strong> {birthString}</span>
              <span>|</span>
              <span><strong style={{color: '#c9d1d9'}}>Origin:</strong> {originCity}</span>
            </div>
          </div>
        </div>

        {/* The Edit Profile Button */}
        <button 
          onClick={() => setIsEditing(true)}
          style={{ 
            background: 'transparent', 
            border: '1px solid #30363d', 
            color: '#8b949e', 
            padding: '10px 15px', 
            borderRadius: '6px', 
            cursor: 'pointer', 
            transition: 'all 0.3s ease',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontSize: '0.8rem'
          }}
          onMouseOver={(e) => { e.currentTarget.style.color = '#ffca28'; e.currentTarget.style.borderColor = '#ffca28'; }}
          onMouseOut={(e) => { e.currentTarget.style.color = '#8b949e'; e.currentTarget.style.borderColor = '#30363d'; }}
        >
          ⚙️ Edit Profile
        </button>
      </div>

      {/* --- PROFILE EDITOR MODAL --- */}
      {isEditing && (
        <div style={{ 
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', 
          background: 'rgba(5, 7, 10, 0.85)', backdropFilter: 'blur(5px)',
          zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' 
        }}>
          <div style={{ 
            background: '#0d1117', padding: '40px', borderRadius: '12px', 
            border: '1px solid #ffca28', width: '90%', maxWidth: '500px', 
            boxShadow: '0 0 40px rgba(255, 202, 40, 0.15)' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
              <h2 style={{ color: '#ffca28', textTransform: 'uppercase', letterSpacing: '2px', margin: 0 }}>
                Update Coordinates
              </h2>
              <button 
                onClick={() => setIsEditing(false)} 
                style={{ background: 'transparent', border: 'none', color: '#8b949e', fontSize: '1.2rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ color: '#8b949e', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Alias / Matrix Name</label>
                <input 
                  type="text" 
                  value={editForm.alias} 
                  onChange={(e) => setEditForm({...editForm, alias: e.target.value})}
                  style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px', boxSizing: 'border-box' }} 
                  placeholder="Master-Builder"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ color: '#8b949e', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Month (MM)</label>
                  <input type="number" min="1" max="12" value={editForm.birth_month} onChange={(e) => setEditForm({...editForm, birth_month: e.target.value})} style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px', boxSizing: 'border-box' }} placeholder="12" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ color: '#8b949e', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Day (DD)</label>
                  <input type="number" min="1" max="31" value={editForm.birth_day} onChange={(e) => setEditForm({...editForm, birth_day: e.target.value})} style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px', boxSizing: 'border-box' }} placeholder="06" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ color: '#8b949e', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>Year (YYYY)</label>
                  <input type="number" value={editForm.birth_year} onChange={(e) => setEditForm({...editForm, birth_year: e.target.value})} style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px', boxSizing: 'border-box' }} placeholder="1961" />
                </div>
              </div>

              <div>
                <label style={{ color: '#8b949e', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>City of Origin</label>
                <input 
                  type="text" 
                  value={editForm.city_of_origin} 
                  onChange={(e) => setEditForm({...editForm, city_of_origin: e.target.value})}
                  style={{ width: '100%', padding: '12px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '6px', boxSizing: 'border-box' }} 
                  placeholder="e.g., Red Deer, Alberta"
                />
              </div>

              <button 
                type="submit" 
                disabled={saving}
                style={{ 
                  background: saving ? '#30363d' : '#ffca28', 
                  color: saving ? '#8b949e' : '#05070a', 
                  border: 'none', 
                  padding: '15px', 
                  fontSize: '1rem', 
                  fontWeight: 'bold', 
                  textTransform: 'uppercase', 
                  borderRadius: '6px', 
                  cursor: saving ? 'not-allowed' : 'pointer', 
                  transition: 'all 0.3s ease',
                  marginTop: '10px'
                }}
              >
                {saving ? "Encrypting Data..." : "Save Coordinates"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* --- 2. MASTER PROGRESS & MEDITATION LOG --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        
        {/* Lifetime Tracking Card */}
        <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '12px', padding: '25px' }}>
          <h3 style={{ color: '#00d4ff', margin: '0 0 15px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1.1rem' }}>
            Ascendance Log
          </h3>
          <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#ffffff', lineHeight: '1' }}>
            {lifetimeCount}
          </div>
          <p style={{ color: '#8b949e', margin: '5px 0 0 0', fontSize: '0.9rem' }}>
            Lifetime Somatic Meditations
          </p>
        </div>

        {/* The 33-Vertebrae Journey */}
        <div style={{ background: '#0d1117', border: '1px solid #00ff41', borderRadius: '12px', padding: '25px', boxShadow: '0 0 20px rgba(0, 255, 65, 0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '15px' }}>
            <div>
              <h3 style={{ color: '#00ff41', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1.1rem' }}>
                Spinal Matrix
              </h3>
              <p style={{ color: '#8b949e', margin: 0, fontSize: '0.85rem' }}>Region: {spinalRegion}</p>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffffff', lineHeight: '1' }}>
              {vertebraLevel} <span style={{ color: '#484f58', fontSize: '1.2rem' }}>/ 33</span>
            </div>
          </div>
          
          <div style={{ width: '100%', background: '#05070a', borderRadius: '20px', height: '12px', border: '1px solid #30363d', overflow: 'hidden', marginBottom: '10px' }}>
            <div style={{ 
              height: '100%', 
              background: 'linear-gradient(90deg, #008020 0%, #00ff41 100%)', 
              width: `${(vertebraLevel / 33) * 100}%`,
              transition: 'width 0.5s ease-out'
            }}></div>
          </div>
          <p style={{ color: '#8b949e', margin: 0, fontSize: '0.75rem', textAlign: 'right', fontStyle: 'italic' }}>10 Logs = 1 Vertebra</p>
        </div>

        {/* Current Month Active Tracker */}
        <div style={{ background: '#0d1117', border: '1px solid #ffca28', borderRadius: '12px', padding: '25px', boxShadow: '0 0 20px rgba(255, 202, 40, 0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '15px' }}>
            <div>
              <h3 style={{ color: '#ffca28', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1.1rem' }}>
                Current Lunar Cycle
              </h3>
              <p style={{ color: '#8b949e', margin: 0, fontSize: '0.85rem' }}>10 Logs Required to Unlock Next Key</p>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffffff', lineHeight: '1' }}>
              {monthlyCount} <span style={{ color: '#484f58', fontSize: '1.2rem' }}>/ 10</span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div style={{ width: '100%', background: '#05070a', borderRadius: '20px', height: '12px', border: '1px solid #30363d', overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ 
              height: '100%', 
              background: 'linear-gradient(90deg, #ff8c1a 0%, #ffca28 100%)', 
              width: `${Math.min((monthlyCount / 10) * 100, 100)}%`,
              transition: 'width 0.5s ease-out'
            }}></div>
          </div>

          <button 
            onClick={onLogMeditation}
            style={{ 
              width: '100%', 
              padding: '12px', 
              background: 'transparent', 
              color: '#ffca28', 
              border: '1px solid #ffca28', 
              borderRadius: '6px', 
              fontWeight: 'bold', 
              textTransform: 'uppercase', 
              letterSpacing: '1px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#ffca28'; e.currentTarget.style.color = '#05070a'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ffca28'; }}
          >
            + Log Today's Somatic Flow
          </button>
        </div>
      </div>

      {/* --- 3. THE 13-KEY VAULT --- */}
      <div style={{ background: '#05070a', border: '1px solid #30363d', borderRadius: '12px', padding: '30px' }}>
        <h3 style={{ color: '#ffffff', margin: '0 0 5px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '1.4rem' }}>
          The Astrological Key Vault
        </h3>
        <p style={{ color: '#8b949e', margin: '0 0 25px 0', fontSize: '0.95rem' }}>
          Earn one key for every month of somatic discipline. ({unlockedKeys.length} / 13 Unlocked)
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '15px' }}>
          {PLANET_ORDER.map((planet) => {
            const isUnlocked = unlockedKeys.includes(planet);
            
            return (
              <div key={planet} style={{ 
                background: isUnlocked ? '#0d1117' : '#05070a', 
                border: isUnlocked ? '1px solid #00ff41' : '1px dashed #30363d', 
                borderRadius: '8px', 
                padding: '20px 10px', 
                textAlign: 'center',
                opacity: isUnlocked ? 1 : 0.4,
                transition: 'all 0.3s ease',
                position: 'relative'
              }}>
                {!isUnlocked && (
                  <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '0.8rem' }}>🔒</div>
                )}
                <div style={{ fontSize: '2rem', color: isUnlocked ? '#00ff41' : '#484f58', marginBottom: '8px' }}>
                  {PLANET_SYMBOLS[planet] || "★"}
                </div>
                <div style={{ color: isUnlocked ? '#ffffff' : '#8b949e', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 'bold' }}>
                  {planet}
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}